/**
 * Composant Analytics pour le plugin Calendar modulaire
 * Dashboard et visualisations pour les données du calendrier
 */

import React, { useState, useMemo } from 'react';
// On garde l'import de CalendarEvent pour la compatibilité avec les Utils, 
// mais on n'importe plus CalendarConfig globalement pour éviter le conflit strict.
import { CalendarEvent } from '../plugins/config/CalendarConfig'; 
import { CalendarAnalytics as CalendarAnalyticsUtils, CalendarExporter } from '../utils/CalendarUtils';

interface CalendarAnalyticsProps {
  // ✅ CORRECTION : Typage flexible pour la config.
  // On demande juste le minimum nécessaire (title, events) et on autorise le reste.
  // Cela résout l'erreur sur 'defaultView' et 'language'.
  config: {
    title?: string;
    events?: CalendarEvent[];
    [key: string]: any; // Permet d'accepter les propriétés du nouveau plugin (ex: viewMode)
  };
  onClose: () => void;
}

export const CalendarAnalytics: React.FC<CalendarAnalyticsProps> = ({ config, onClose }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'trends' | 'export'>('overview');
  const [exportFormat, setExportFormat] = useState<'csv' | 'ics'>('csv');

  // Calculer les analytics
  const analytics = useMemo(() => {
    return CalendarAnalyticsUtils.generateAnalytics(config.events || []);
  }, [config.events]);

  // Fonction d'export
  const handleExport = async () => {
    try {
      if (exportFormat === 'csv') {
        const csvContent = await CalendarExporter.exportToCSV(config.events || []);
        CalendarExporter.downloadFile(
          csvContent, 
          `calendrier_${new Date().toISOString().split('T')[0]}.csv`, 
          'text/csv'
        );
      } else if (exportFormat === 'ics') {
        // Fallback sur le titre si undefined
        const safeTitle = config.title || 'Export Calendrier';
        const icsContent = await CalendarExporter.exportToICS(config.events || [], safeTitle);
        CalendarExporter.downloadFile(
          icsContent, 
          `calendrier_${new Date().toISOString().split('T')[0]}.ics`, 
          'text/calendar'
        );
      }
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Erreur lors de l\'export des données');
    }
  };

  // Styles pour les graphiques en barres simples
  const BarChart: React.FC<{ data: { label: string; value: number; color?: string }[]; maxValue?: number }> = ({ data, maxValue }) => {
    const max = maxValue || Math.max(...data.map(d => d.value));
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {data.map((item, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ minWidth: '80px', fontSize: '12px', fontWeight: 'bold' }}>
              {item.label}
            </div>
            <div style={{ 
              flex: 1, 
              height: '20px', 
              backgroundColor: '#f0f0f0', 
              borderRadius: '10px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${max > 0 ? (item.value / max) * 100 : 0}%`,
                height: '100%',
                backgroundColor: item.color || '#0d6efd',
                borderRadius: '10px',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ minWidth: '30px', fontSize: '12px', fontWeight: 'bold' }}>
              {item.value}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '900px',
        height: '90%',
        maxHeight: '700px',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: '#0d6efd',
          color: 'white',
          padding: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h4 style={{ margin: 0, fontWeight: 'bold' }}>📊 Analytics Calendrier</h4>
            <p style={{ margin: '4px 0 0 0', opacity: 0.9, fontSize: '14px' }}>
              {config.title || 'Calendrier'} • {analytics.totalEvents} événements
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              borderRadius: '6px',
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ✕ Fermer
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          backgroundColor: '#f8f9fa'
        }}>
          {[
            { id: 'overview', label: '📈 Vue d\'ensemble', icon: '📊' },
            { id: 'trends', label: '📅 Tendances', icon: '📈' },
            { id: 'export', label: '💾 Export', icon: '📤' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '12px 20px',
                border: 'none',
                backgroundColor: activeTab === tab.id ? 'white' : 'transparent',
                borderBottom: activeTab === tab.id ? '2px solid #0d6efd' : '2px solid transparent',
                cursor: 'pointer',
                fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                color: activeTab === tab.id ? '#0d6efd' : '#6c757d',
                fontSize: '14px'
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* KPIs */}
              <div>
                <h5 style={{ marginBottom: '16px', color: '#333' }}>📋 Indicateurs clés</h5>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                  gap: '16px' 
                }}>
                  {[
                    { label: 'Total événements', value: analytics.totalEvents, icon: '📅', color: '#0d6efd' },
                    { label: 'À venir', value: analytics.upcomingEvents, icon: '⏭️', color: '#28a745' },
                    { label: 'Passés', value: analytics.pastEvents, icon: '⏮️', color: '#6c757d' },
                    { label: 'Priorité haute', value: analytics.highPriorityEvents, icon: '🔴', color: '#dc3545' },
                    { label: 'Toute la journée', value: analytics.allDayEvents, icon: '📆', color: '#ffc107' },
                    { label: 'Avec lieu', value: analytics.eventsWithLocation, icon: '📍', color: '#17a2b8' }
                  ].map((kpi, index) => (
                    <div key={index} style={{
                      backgroundColor: 'white',
                      border: `2px solid ${kpi.color}`,
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>{kpi.icon}</div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: kpi.color }}>
                        {kpi.value}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                        {kpi.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Distribution des catégories */}
              <div>
                <h5 style={{ marginBottom: '16px', color: '#333' }}>🗂️ Répartition par catégories</h5>
                <div style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '16px' }}>
                  <BarChart 
                    data={analytics.categoryDistribution.map(cat => ({
                      label: cat.category,
                      value: cat.count,
                      color: '#0d6efd'
                    }))}
                  />
                </div>
              </div>

              {/* Distribution des priorités */}
              <div>
                <h5 style={{ marginBottom: '16px', color: '#333' }}>⚡ Répartition par priorités</h5>
                <div style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '16px' }}>
                  <BarChart 
                    data={analytics.priorityDistribution.map(priority => ({
                      label: priority.priority === 'high' ? '🔴 Haute' : 
                              priority.priority === 'low' ? '🟢 Basse' : '🟡 Normale',
                      value: priority.count,
                      color: priority.priority === 'high' ? '#dc3545' : 
                             priority.priority === 'low' ? '#28a745' : '#ffc107'
                    }))}
                  />
                </div>
              </div>

              {/* Jours chargés */}
              {analytics.busyDays.length > 0 && (
                <div>
                  <h5 style={{ marginBottom: '16px', color: '#333' }}>🔥 Jours chargés (3+ événements)</h5>
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '8px' 
                  }}>
                    {analytics.busyDays.slice(0, 10).map(date => (
                      <span
                        key={date}
                        style={{
                          backgroundColor: '#fff3cd',
                          border: '1px solid #ffeaa7',
                          borderRadius: '16px',
                          padding: '4px 12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#856404'
                        }}
                      >
                        {new Date(date).toLocaleDateString('fr-FR')}
                      </span>
                    ))}
                    {analytics.busyDays.length > 10 && (
                      <span style={{ fontSize: '12px', color: '#6c757d', padding: '4px 8px' }}>
                        +{analytics.busyDays.length - 10} autres...
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'trends' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Tendances mensuelles */}
              <div>
                <h5 style={{ marginBottom: '16px', color: '#333' }}>📈 Évolution mensuelle</h5>
                <div style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '16px' }}>
                  {analytics.monthlyTrends.length > 0 ? (
                    <BarChart 
                      data={analytics.monthlyTrends.map(trend => ({
                        label: new Date(trend.month + '-01').toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
                        value: trend.count,
                        color: '#17a2b8'
                      }))}
                    />
                  ) : (
                    <p style={{ textAlign: 'center', color: '#6c757d', margin: 0 }}>
                      Pas assez de données pour afficher les tendances
                    </p>
                  )}
                </div>
              </div>

              {/* Distribution hebdomadaire */}
              <div>
                <h5 style={{ marginBottom: '16px', color: '#333' }}>📅 Répartition par jour de la semaine</h5>
                <div style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '16px' }}>
                  <BarChart 
                    data={analytics.weeklyDistribution.map(day => ({
                      label: day.day.substring(0, 3),
                      value: day.count,
                      color: '#28a745'
                    }))}
                  />
                </div>
              </div>

              {/* Statistiques avancées */}
              <div>
                <h5 style={{ marginBottom: '16px', color: '#333' }}>📊 Statistiques avancées</h5>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '16px' 
                }}>
                  <div style={{ backgroundColor: '#e3f2fd', borderRadius: '8px', padding: '16px' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1976d2' }}>
                      {analytics.averageEventsPerWeek}
                    </div>
                    <div style={{ fontSize: '14px', color: '#1565c0' }}>
                      Événements par semaine (moyenne)
                    </div>
                  </div>
                  
                  <div style={{ backgroundColor: '#f3e5f5', borderRadius: '8px', padding: '16px' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#7b1fa2' }}>
                      {analytics.recurringEvents}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6a1b9a' }}>
                      Événements récurrents
                    </div>
                  </div>
                  
                  <div style={{ backgroundColor: '#e8f5e8', borderRadius: '8px', padding: '16px' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#2e7d32' }}>
                      {Math.round((analytics.eventsWithLocation / Math.max(1, analytics.totalEvents)) * 100)}%
                    </div>
                    <div style={{ fontSize: '14px', color: '#1b5e20' }}>
                      Événements avec localisation
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'export' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <h5 style={{ marginBottom: '16px', color: '#333' }}>💾 Exporter les données</h5>
                <p style={{ color: '#6c757d', marginBottom: '20px' }}>
                  Exportez vos événements dans différents formats pour les utiliser dans d'autres applications.
                </p>
              </div>

              <div style={{ 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px', 
                padding: '20px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                    Format d'export
                  </label>
                  <select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value as any)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="csv">📊 CSV (Excel, Google Sheets)</option>
                    <option value="ics">📅 ICS (iCal, Outlook, Google Calendar)</option>
                  </select>
                </div>

                <div style={{ 
                  backgroundColor: 'white', 
                  borderRadius: '6px', 
                  padding: '16px',
                  marginBottom: '16px',
                  border: '1px solid #e3e6ea'
                }}>
                  <h6 style={{ margin: '0 0 8px 0', color: '#495057' }}>
                    {exportFormat === 'csv' ? '📊 Export CSV' : '📅 Export iCal'}
                  </h6>
                  <p style={{ margin: 0, fontSize: '13px', color: '#6c757d' }}>
                    {exportFormat === 'csv' 
                      ? 'Format tableur compatible avec Excel, Google Sheets et autres logiciels de traitement de données.'
                      : 'Format calendrier standard compatible avec Outlook, Google Calendar, Apple Calendar et autres applications de calendrier.'
                    }
                  </p>
                </div>

                <button
                  onClick={handleExport}
                  disabled={!config.events || config.events.length === 0}
                  style={{
                    backgroundColor: config.events && config.events.length > 0 ? '#28a745' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px 24px',
                    cursor: config.events && config.events.length > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    width: '100%'
                  }}
                >
                  {config.events && config.events.length > 0 
                    ? `📤 Exporter ${config.events.length} événement${config.events.length > 1 ? 's' : ''}`
                    : '❌ Aucun événement à exporter'
                  }
                </button>
              </div>

              {/* Informations sur l'export */}
              <div style={{ 
                backgroundColor: '#d1ecf1', 
                border: '1px solid #bee5eb',
                borderRadius: '6px', 
                padding: '12px' 
              }}>
                <div style={{ fontSize: '14px', color: '#0c5460' }}>
                  <strong>ℹ️ Informations :</strong>
                  <ul style={{ margin: '8px 0 0 16px', paddingLeft: 0 }}>
                    <li>Les données exportées incluent tous les détails des événements</li>
                    <li>L'export CSV peut être ouvert dans Excel ou Google Sheets</li>
                    <li>L'export iCal peut être importé dans votre application de calendrier préférée</li>
                    <li>Les fichiers sont générés et téléchargés automatiquement</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarAnalytics;
